import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/store/root-state';
import { logoutUser } from '@/features/auth';
import {
  confirmBusinessUser,
  fetchBusinessUsersPool,
  updateBusinessUser,
  updateBusinessUserEnabled,
} from './businessUsersPoolThunks';
import type { BusinessUserSummary, BusinessUsersPoolState } from './businessUsersPoolTypes';

const initialState: BusinessUsersPoolState = {
  users: [],
  isLoading: false,
  error: null,
  hasFetched: false,
  nextToken: null,
  hasMore: true,
  isUpdating: false,
  updateError: null,
};

function getBusinessUserKey(user: BusinessUserSummary) {
  return user.username?.trim() || user.email?.trim() || '';
}

function mergeUsers(existing: BusinessUserSummary[], incoming: BusinessUserSummary[]) {
  const next = [...existing];

  incoming.forEach((user) => {
    const key = getBusinessUserKey(user);
    if (!key) {
      next.push(user);
      return;
    }

    const index = next.findIndex((candidate) => getBusinessUserKey(candidate) === key);
    if (index >= 0) {
      next[index] = user;
      return;
    }

    next.push(user);
  });

  return next;
}

const businessUsersPoolSlice = createSlice({
  name: 'businessUsersPool',
  initialState,
  reducers: {
    clearBusinessUsersPoolState: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessUsersPool.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBusinessUsersPool.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasFetched = true;
        state.error = null;
        state.nextToken = action.payload.nextToken;
        state.hasMore = action.payload.nextToken !== null;

        if (action.meta.arg?.nextToken) {
          state.users = mergeUsers(state.users, action.payload.users);
          return;
        }

        state.users = action.payload.users;
      })
      .addCase(fetchBusinessUsersPool.rejected, (state, action) => {
        if (action.meta.condition) {
          return;
        }

        state.isLoading = false;
        state.hasFetched = true;
        state.error = action.payload?.message || action.error.message || 'Failed to load business users pool';
      })
      .addCase(updateBusinessUser.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateBusinessUser.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.updateError = null;
        state.users = mergeUsers(state.users, [action.payload.user]);
      })
      .addCase(updateBusinessUser.rejected, (state, action) => {
        if (action.meta.condition) {
          return;
        }

        state.isUpdating = false;
        state.updateError = action.payload?.message || action.error.message || 'Failed to update business user';
      })
      .addCase(updateBusinessUserEnabled.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateBusinessUserEnabled.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.updateError = null;
        state.users = mergeUsers(state.users, [action.payload.user]);
      })
      .addCase(updateBusinessUserEnabled.rejected, (state, action) => {
        if (action.meta.condition) {
          return;
        }

        state.isUpdating = false;
        state.updateError =
          action.payload?.message || action.error.message || 'Failed to update business user enabled state';
      })
      .addCase(confirmBusinessUser.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(confirmBusinessUser.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.updateError = null;
        state.users = mergeUsers(state.users, [action.payload.user]);
      })
      .addCase(confirmBusinessUser.rejected, (state, action) => {
        if (action.meta.condition) {
          return;
        }

        state.isUpdating = false;
        state.updateError = action.payload?.message || action.error.message || 'Failed to confirm business user';
      })
      .addCase(logoutUser.fulfilled, () => ({ ...initialState }));
  },
});

const selectBusinessUsersPoolState = (state: RootState) => state.businessUsersPool;

export const selectBusinessUsersPoolUsers = createSelector([selectBusinessUsersPoolState], (state) => state.users);

export const selectBusinessUsersPoolLoading = createSelector(
  [selectBusinessUsersPoolState],
  (state) => state.isLoading,
);

export const selectBusinessUsersPoolError = createSelector([selectBusinessUsersPoolState], (state) => state.error);

export const selectBusinessUsersPoolHasFetched = createSelector(
  [selectBusinessUsersPoolState],
  (state) => state.hasFetched,
);

export const selectBusinessUsersPoolHasMore = createSelector([selectBusinessUsersPoolState], (state) => state.hasMore);

export const selectBusinessUsersPoolNextToken = createSelector(
  [selectBusinessUsersPoolState],
  (state) => state.nextToken,
);

export const selectBusinessUsersPoolUpdating = createSelector(
  [selectBusinessUsersPoolState],
  (state) => state.isUpdating,
);

export const selectBusinessUsersPoolUpdateError = createSelector(
  [selectBusinessUsersPoolState],
  (state) => state.updateError,
);

export const { clearBusinessUsersPoolState } = businessUsersPoolSlice.actions;
export default businessUsersPoolSlice.reducer;
